import React, { useEffect, useState } from "react";

import { useAccelContext } from "../../contexts/AccelContext/AccelContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";
import { CircularProgress, Box, Typography } from "@mui/material";
import { scaleBand } from "d3-scale";

// Function to generate fake data
const generateFakeData = () => {
  return Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    netProfit: Math.floor(Math.random() * 10000),
    totalExpenses: Math.floor(Math.random() * 5000),
    totalRevenue: Math.floor(Math.random() * 15000),
  }));
};

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active: boolean;
  payload: any[];
  label: string;
}) => {
  const { theme } = useAccelContext();

  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: theme?.accent?.hex || "#000000",
          padding: "10px",
          borderRadius: "8px",
          color: theme?.text?.hex || "#FFFFFF",
        }}
      >
        <Typography variant="body2">{`Month: ${label}`}</Typography>
        {payload.map((entry, index) => (
          <Typography key={`item-${index}`} variant="body2">
            {`${entry.name}: ${entry.value}`}
          </Typography>
        ))}
      </Box>
    );
  }

  return null;
};

export const AccelBarChart = () => {
  const { fillerData, theme, profitandlossYearly } = useAccelContext();

  const currentMonth = new Date().getMonth() + 1; // Get current month (1-12)
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  useEffect(() => {
    if (profitandlossYearly) {
      
    }
    // Set isFirstLoad to false after the first render
    setIsFirstLoad(false);
  }, [profitandlossYearly]);

  

  if (!profitandlossYearly && !fillerData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={197}
      >
        <CircularProgress sx={{ color: theme?.primary?.hex }} />
      </Box>
    );
  }

  const chartData = fillerData
    ? generateFakeData().slice(0, currentMonth)
    : (profitandlossYearly ?? []).filter((item) => item.month <= currentMonth).slice(-7);

  const monthFormatter = (monthNumber: number) => {
    // Create a date object using the month number, assuming the year is irrelevant
    const date = new Date(2000, monthNumber - 1);
    // Format the date to get the month name
    return format(date, "MMM");
  };

  // Calculate the ticks for the Y-axis
  const maxDataValue = Math.max(
    ...chartData.map((d) =>
      Math.max(d.netProfit, d.totalExpenses, d.totalRevenue)
    )
  );
  const tickInterval = maxDataValue > 50000 ? 5000 : 1000; // Use 5000 if maxDataValue is greater than 50000, otherwise 1000
  const yAxisTicks = Array.from(
    { length: Math.ceil(maxDataValue / tickInterval) + 1 },
    (_, i) => Math.floor(i * tickInterval)
  );

  return (
    <ResponsiveContainer
      width="100%"
      aspect={3}
      style={{ background: theme?.accent?.hex || "#000000" }}
    >
      <BarChart
        data={chartData}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }} // Adjust margins to ensure full width
        barCategoryGap={0} // No space between the bars for each month
      >
        <XAxis
          dataKey="month"
          tickFormatter={monthFormatter}
          tick={{
            fontSize: 14,
            fontFamily: "Helvetica Neue",
            fill: theme?.secondary?.hex,
            textAnchor: "middle", // Center the text
          }}
          tickCount={currentMonth} // Set tickCount to currentMonth
          tickLine={false}
          axisLine={{ stroke: theme?.secondary?.hex, strokeWidth: 1 }}
          interval={0}
          scale={scaleBand().range([0, 1]).paddingInner(0).paddingOuter(0)}
          padding={{ left: 0, right: 0 }}
        />
        
        <Tooltip
          content={<CustomTooltip active={true} payload={[]} label="" />}
        />
        <Bar
          dataKey="netProfit"
          fill={theme?.positive?.hex}
          radius={[10, 10, 0, 0]}
          barSize={10}
          isAnimationActive={isFirstLoad}
        />
        <Bar
          dataKey="totalExpenses"
          fill={theme?.negative?.hex}
          radius={[10, 10, 0, 0]}
          barSize={10}
          isAnimationActive={isFirstLoad}
        />
        <Bar
          dataKey="totalRevenue"
          fill={theme?.primary?.hex}
          radius={[10, 10, 0, 0]}
          barSize={10}
          isAnimationActive={isFirstLoad}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default AccelBarChart;
